import React, { useEffect, useMemo, useState } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS, Step } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { CustomTooltip } from './CustomTooltip';
import {
  WELCOME_TOUR_STEPS,
  moveTourIndex,
  routePath,
} from './tourPlan';

export const OnboardingTour: React.FC = () => {
  const {
    active,
    step,
    completed,
    goToStep,
    skipTour,
    completeOnboarding,
  } = useOnboarding();
  const location = useLocation();
  const navigate = useNavigate();
  const [navigating, setNavigating] = useState(false);

  const plan = WELCOME_TOUR_STEPS[step] ?? WELCOME_TOUR_STEPS[0];
  const steps: Step[] = useMemo(() => WELCOME_TOUR_STEPS.map((item) => ({
    target: item.target,
    placement: item.target === 'body' ? 'center' : 'bottom',
    disableBeacon: true,
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
        <p>{item.body}</p>
      </div>
    ),
  })), []);

  useEffect(() => {
    if (!active || completed) return;
    const wanted = routePath(plan.route);
    if (!wanted || location.pathname === wanted) {
      setNavigating(false);
      return;
    }
    setNavigating(true);
    navigate(plan.route || wanted);
  }, [active, completed, location.pathname, navigate, plan.route]);

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') skipTour();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, skipTour]);

  const isPresent = (selector: string) => Boolean(document.querySelector(selector));

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, status, type } = data;

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const next = moveTourIndex(step, action === ACTIONS.PREV ? -1 : 1, isPresent);
      if (next === step) {
        skipTour();
        return;
      }
      goToStep(next);
      return;
    }

    if (status === STATUS.FINISHED) {
      completeOnboarding();
      navigate('/dashboard');
      return;
    }
    if (status === STATUS.SKIPPED) {
      skipTour();
      return;
    }
    if (type !== EVENTS.STEP_AFTER) return;

    if (action === ACTIONS.PREV) {
      const next = moveTourIndex(step, -1, isPresent);
      goToStep(next);
      return;
    }
    if (action === ACTIONS.NEXT || action === ACTIONS.SKIP) {
      if (step >= WELCOME_TOUR_STEPS.length - 1) {
        completeOnboarding();
        navigate('/dashboard');
        return;
      }
      const next = moveTourIndex(step, 1, isPresent);
      if (next === step) {
        completeOnboarding();
        return;
      }
      goToStep(next);
    }
  };

  if (!active || completed || navigating) return null;

  return (
    <Joyride
      steps={steps}
      run={active}
      stepIndex={step}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      disableOverlayClose={false}
      callback={handleJoyrideCallback}
      tooltipComponent={(props) => (
        <CustomTooltip {...props} totalSteps={steps.length} />
      )}
      styles={{
        options: {
          primaryColor: 'hsl(var(--primary))',
          backgroundColor: 'hsl(var(--background))',
          textColor: 'hsl(var(--foreground))',
          zIndex: 10000,
        },
      }}
    />
  );
};
